import { api } from './client';
import type { Message } from '@/types';

export interface UnifiedInboxResponse {
  messages: Message[];
}

export async function getUnifiedInbox(view: 'personal' | 'all' | 'review' | 'spam'): Promise<UnifiedInboxResponse> {
  return api.get<UnifiedInboxResponse>(`/inbox/${view}`);
}

export async function getBusinessInbox(businessId: string): Promise<UnifiedInboxResponse> {
  return api.get<UnifiedInboxResponse>(`/inbox/business/${businessId}`);
}

export async function searchMessages(query: string, view?: string): Promise<UnifiedInboxResponse> {
  return api.get<UnifiedInboxResponse>(`/inbox/search?q=${encodeURIComponent(query)}${view ? `&view=${encodeURIComponent(view)}` : ''}`);
}

export async function sweepInbox(mailboxId?: string, folder?: string): Promise<{ swept: number }> {
  const body: any = {};
  if (mailboxId) body.mailboxId = mailboxId;
  if (folder) body.folder = folder;
  return api.post<{ swept: number }>('/inbox/sweep', body);
}

export async function syncAll(): Promise<{ ok: boolean; synced: number; failed: number; newCount: number; spamCount: number }> {
  return api.post('/inbox/sync', {});
}

export interface InboxCounts {
  review: number;
  spam: number;
  novelSenders: number;
}

export async function getInboxCounts(): Promise<InboxCounts> {
  return api.get<InboxCounts>('/inbox/counts');
}

export interface DeletedMessage {
  id: string;
  mailboxId: string;
  mailboxEmail: string;
  messageId: string | null;
  fromEmail: string | null;
  fromName: string | null;
  toEmail: string | null;
  ccEmail: string | null;
  subject: string | null;
  date: number | null;
  snippet: string | null;
  bodyText: string | null;
  hasAttachments: boolean;
  deletedAt: number;
  expiresAt: number;
}

export interface DeletedResponse {
  retentionDays: number;
  messages: DeletedMessage[];
}

export async function getDeleted(mailboxId?: string): Promise<DeletedResponse> {
  return api.get<DeletedResponse>(`/inbox/deleted${mailboxId ? `?mailboxId=${encodeURIComponent(mailboxId)}` : ''}`);
}

export interface SentMessage {
  id: string;
  mailboxId: string;
  mailboxEmail: string;
  messageId: string | null;
  to: string[];
  subject: string | null;
  snippet: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  hasAttachments: boolean;
  sentAt: number;
  // [013] Sent now also carries messages pulled from the account's real Sent folder
  // (sent from Roundcube, a phone, anywhere), not just b-mail's own sends.
  fromEmail: string | null;
  uid: number | null;
  folder: string | null;
  origin: 'bmail' | 'imap' | 'both';
}

export interface SentAttachment {
  id: string;
  filename: string | null;
  contentType: string | null;
  size: number;
}

export async function getSentAttachments(sentId: string): Promise<{ attachments: SentAttachment[] }> {
  return api.get<{ attachments: SentAttachment[] }>(`/inbox/sent/${encodeURIComponent(sentId)}/attachments`);
}

export async function getSent(mailboxId?: string): Promise<{ messages: SentMessage[] }> {
  return api.get<{ messages: SentMessage[] }>(`/inbox/sent${mailboxId ? `?mailboxId=${encodeURIComponent(mailboxId)}` : ''}`);
}

export async function restoreDeleted(mailboxId: string, id: string): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/mailboxes/${mailboxId}/deleted/${id}/restore`, {});
}

export async function deleteForever(mailboxId: string, id: string): Promise<{ ok: boolean }> {
  return api.delete<{ ok: boolean }>(`/mailboxes/${mailboxId}/deleted/${id}`);
}
// ---- Drafts ----

export interface Draft {
  id: string;
  mailboxId: string | null;
  mailboxEmail: string | null;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  bodyHtml: string;
  attachments?: { filename: string; contentType: string; size: number; content: string }[];
  updatedAt: number;
}

export async function getDrafts(): Promise<{ drafts: Draft[] }> {
  return api.get<{ drafts: Draft[] }>('/inbox/drafts');
}

export async function saveDraft(input: { id?: string; mailboxId?: string | null; to: string[]; cc?: string[]; bcc?: string[]; subject: string; bodyHtml: string; attachments?: { filename: string; contentType: string; content: string }[] }): Promise<{ ok: boolean; id: string }> {
  return api.post<{ ok: boolean; id: string }>('/inbox/drafts', input);
}

export async function deleteDraft(id: string): Promise<{ ok: boolean }> {
  return api.delete<{ ok: boolean }>(`/inbox/drafts/${id}`);
}
